import React, { useCallback, useEffect, useState } from 'react';
import api from './api';
import {
  formatMontant,
  formatDate,
  isoToDisplayDate,
  displayDateToIso,
  taskCode,
  EXPENSE_CATEGORIES,
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_STATUSES,
  EXPENSE_STATUS_LABELS,
  EXPENSE_STATUS_BADGE,
  CAN_DELETE_ANY_EXPENSE_ROLES,
} from './helpers';

const LIGNE_VIDE = {
  category: 'sortie_magasin',
  status: 'engage',
  label: '',
  reference: '',
  supplier: '',
  amount: '',
  date: '',
  task_id: '',
};

/**
 * Suivi budgétaire d'un projet.
 *
 * Deux montants distincts, qui ne bougent pas au même moment :
 *   engagé  = l'argent est réservé (bon de sortie émis, commande passée)
 *   dépensé = l'argent est effectivement sorti (pièce livrée, facture réglée)
 * Le « reste disponible » se calcule sur l'engagé : l'argent déjà commandé
 * n'est plus disponible, même s'il n'est pas encore facturé.
 */
function ProjectBudget({ project, tasks, currentUser, onBudgetChanged }) {
  const [depenses, setDepenses] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [formulaire, setFormulaire] = useState(null); // null = ferme
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(() => {
    setChargement(true);
    api
      .get('/expenses', { params: { projectId: project.id } })
      .then((res) => setDepenses(res.data))
      .catch(() => setErreur('Impossible de charger les dépenses.'))
      .finally(() => setChargement(false));
  }, [project.id]);

  useEffect(() => { charger(); }, [charger]);

  const alloue = project.budget_allocated || 0;
  const engage = depenses.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const depense = depenses
    .filter((d) => d.status === 'depense')
    .reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const reste = alloue - engage;
  const pctDepense = alloue ? Math.round((depense / alloue) * 100) : 0;
  const pctEngage = alloue ? Math.round((engage / alloue) * 100) : 0;
  const avancement = project.progress_percent || 0;
  const ecart = pctDepense - avancement;

  const parCategorie = EXPENSE_CATEGORIES.map((c) => ({
    categorie: c,
    total: depenses.filter((d) => d.category === c).reduce((s, d) => s + (Number(d.amount) || 0), 0),
    nombre: depenses.filter((d) => d.category === c).length,
  })).filter((c) => c.nombre > 0);

  const ouvrirFormulaire = (depenseExistante) => {
    setErreur('');
    if (depenseExistante) {
      setFormulaire({
        ...depenseExistante,
        amount: String(depenseExistante.amount ?? ''),
        date: isoToDisplayDate(depenseExistante.date),
        task_id: depenseExistante.task_id || '',
      });
    } else {
      setFormulaire({ ...LIGNE_VIDE });
    }
  };

  const champ = (nom) => (e) => setFormulaire({ ...formulaire, [nom]: e.target.value });

  const enregistrer = (e) => {
    e.preventDefault();
    setErreur('');

    const montant = parseFloat(String(formulaire.amount).replace(',', '.'));
    if (!formulaire.label.trim()) return setErreur('Indiquez un libellé pour cette dépense.');
    if (!Number.isFinite(montant) || montant <= 0) return setErreur('Le montant doit être supérieur à zéro.');

    // La date est facultative, mais si elle est saisie elle doit être valide :
    // une date impossible fausserait ensuite tous les tris.
    let dateIso = null;
    if (formulaire.date) {
      dateIso = displayDateToIso(formulaire.date);
      if (!dateIso) return setErreur('La date doit être au format jj/mm/aaaa.');
    }

    const corps = {
      project_id: project.id,
      category: formulaire.category,
      status: formulaire.status,
      label: formulaire.label.trim(),
      reference: formulaire.reference.trim(),
      supplier: formulaire.supplier.trim(),
      amount: montant,
      date: dateIso,
      task_id: formulaire.task_id || null,
    };

    setEnregistrement(true);
    const requete = formulaire.id
      ? api.put(`/expenses/${formulaire.id}`, corps)
      : api.post('/expenses', corps);

    requete
      .then(() => {
        setFormulaire(null);
        charger();
        if (onBudgetChanged) onBudgetChanged();
      })
      .catch((err) => setErreur(err.response?.data?.message || "L'enregistrement a échoué."))
      .finally(() => setEnregistrement(false));
  };

  const supprimer = (d) => {
    setErreur('');
    api
      .delete(`/expenses/${d.id}`)
      .then(() => {
        charger();
        if (onBudgetChanged) onBudgetChanged();
      })
      .catch((err) => setErreur(err.response?.data?.message || 'La suppression a échoué.'));
  };

  const peutSupprimer = (d) =>
    d.created_by === currentUser?.id || CAN_DELETE_ANY_EXPENSE_ROLES.includes(currentUser?.role);

  if (chargement) return <p>Chargement du budget...</p>;

  return (
    <div className="fade-in">
      {erreur && <div className="error-message" style={{ marginBottom: 16 }}>{erreur}</div>}

      {alloue === 0 && (
        <div className="empty-state" style={{ marginBottom: 16, textAlign: 'left' }}>
          Aucun budget alloué n'est renseigné pour ce projet. Les dépenses ci-dessous
          seront bien enregistrées, mais les pourcentages resteront vides tant que
          l'enveloppe n'est pas saisie dans « Modifier le projet ».
        </div>
      )}

      <div className="budget-stats">
        <div className="budget-stat">
          <div className="detail-stat-label">Budget alloué</div>
          <div className="detail-stat-value">{formatMontant(alloue)}</div>
        </div>
        <div className="budget-stat">
          <div className="detail-stat-label">Engagé</div>
          <div className="detail-stat-value" style={{ color: 'var(--accent)' }}>{formatMontant(engage)}</div>
          {alloue > 0 && <div className="budget-stat-sub">{pctEngage}% de l’enveloppe</div>}
        </div>
        <div className="budget-stat">
          <div className="detail-stat-label">Dépensé</div>
          <div className="detail-stat-value">{formatMontant(depense)}</div>
          {alloue > 0 && <div className="budget-stat-sub">{pctDepense}% de l’enveloppe</div>}
        </div>
        <div className="budget-stat">
          <div className="detail-stat-label">Reste disponible</div>
          <div className="detail-stat-value" style={{ color: reste < 0 ? 'var(--danger)' : 'var(--success)' }}>
            {formatMontant(reste)}
          </div>
          <div className="budget-stat-sub">engagé déduit</div>
        </div>
      </div>

      {alloue > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="budget-jauge-ligne">
            <span className="budget-jauge-label">Budget dépensé</span>
            <div className="progress-bar" style={{ margin: 0, flex: 1 }}>
              <div className="progress-fill" style={{ width: `${Math.min(pctDepense, 100)}%` }} />
            </div>
            <span className="budget-jauge-valeur">{pctDepense}%</span>
          </div>
          <div className="budget-jauge-ligne">
            <span className="budget-jauge-label">Avancement des tâches</span>
            <div className="progress-bar" style={{ margin: 0, flex: 1 }}>
              <div
                className="progress-fill"
                style={{ width: `${Math.min(avancement, 100)}%`, background: 'var(--secondary)' }}
              />
            </div>
            <span className="budget-jauge-valeur">{avancement}%</span>
          </div>

          {/* Le signal le plus précoce d'une dérive : consommer nettement plus
              de budget que d'avancement physique. */}
          <div className={`budget-ecart ${ecart >= 15 ? 'budget-ecart-alerte' : ''}`}>
            {ecart >= 15
              ? `Attention : ${ecart} points de budget consommés de plus que d’avancement réalisé.`
              : ecart <= -15
              ? `L’avancement dépasse la consommation de ${Math.abs(ecart)} points — projet économe à ce stade.`
              : 'Consommation et avancement restent cohérents.'}
          </div>

          {reste < 0 && (
            <div className="error-message" style={{ marginTop: 12 }}>
              <strong>Enveloppe dépassée</strong> de {formatMontant(Math.abs(reste))} en engagements.
            </div>
          )}
        </div>
      )}

      {parCategorie.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 12, fontSize: 14 }}>Répartition par nature</h3>
          <div className="budget-categories">
            {parCategorie.map((c) => (
              <div key={c.categorie} className="budget-categorie">
                <div className="budget-categorie-label">{EXPENSE_CATEGORY_LABELS[c.categorie]}</div>
                <div className="budget-categorie-total">{formatMontant(c.total)}</div>
                <div className="budget-stat-sub">{c.nombre} ligne{c.nombre > 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="page-header-row">
        <h3 style={{ fontSize: 14 }}>
          Dépenses enregistrées {depenses.length > 0 && `(${depenses.length})`}
        </h3>
        {!formulaire && (
          <button className="btn btn-primary" onClick={() => ouvrirFormulaire(null)}>
            + Ajouter une dépense
          </button>
        )}
      </div>

      {formulaire && (
        <form className="card" style={{ marginBottom: 16 }} onSubmit={enregistrer}>
          <div className="form-row">
            <div className="form-group">
              <label>Nature</label>
              <select value={formulaire.category} onChange={champ('category')}>
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{EXPENSE_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select value={formulaire.status} onChange={champ('status')}>
                {EXPENSE_STATUSES.map((s) => (
                  <option key={s} value={s}>{EXPENSE_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Libellé</label>
            <input
              value={formulaire.label}
              onChange={champ('label')}
              placeholder="Ex : roulements SKF pour convoyeur CV-04"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Référence <span style={{ fontWeight: 400, textTransform: 'none' }}>(bon de sortie, OT, facture)</span></label>
              <input value={formulaire.reference} onChange={champ('reference')} placeholder="Ex : BS-2026-0142" />
            </div>
            <div className="form-group">
              <label>Fournisseur / atelier</label>
              <input value={formulaire.supplier} onChange={champ('supplier')} placeholder="Ex : Magasin central" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Montant (MAD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formulaire.amount}
                onChange={champ('amount')}
              />
            </div>
            <div className="form-group">
              <label>Date</label>
              <input placeholder="jj/mm/aaaa" value={formulaire.date} onChange={champ('date')} />
            </div>
          </div>

          <div className="form-group">
            <label>Tâche concernée <span style={{ fontWeight: 400, textTransform: 'none' }}>(facultatif)</span></label>
            <select value={formulaire.task_id} onChange={champ('task_id')}>
              <option value="">— Aucune —</option>
              {(tasks || []).map((t) => (
                <option key={t.id} value={t.id}>{taskCode(t.id)} · {t.name}</option>
              ))}
            </select>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn" onClick={() => setFormulaire(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={enregistrement}>
              {enregistrement ? 'Enregistrement...' : formulaire.id ? 'Mettre à jour' : 'Enregistrer'}
            </button>
          </div>
        </form>
      )}

      {depenses.length === 0 && !formulaire && (
        <div className="empty-state">
          Aucune dépense enregistrée. Ajoutez les sorties magasin, les interventions
          de maintenance et les prestations externes rattachées à ce projet.
        </div>
      )}

      {depenses.length > 0 && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Nature</th>
                <th>Libellé</th>
                <th>Référence</th>
                <th>Tâche</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {depenses.map((d) => (
                <tr key={d.id}>
                  <td>{d.date ? formatDate(d.date) : '-'}</td>
                  <td>{EXPENSE_CATEGORY_LABELS[d.category] || d.category}</td>
                  <td>
                    {d.label}
                    {d.supplier && <div className="budget-stat-sub">{d.supplier}</div>}
                  </td>
                  <td>{d.reference || '-'}</td>
                  <td>{d.task_id ? taskCode(d.task_id) : '-'}</td>
                  <td>
                    <span className={`badge ${EXPENSE_STATUS_BADGE[d.status] || 'badge-todo'}`}>
                      {EXPENSE_STATUS_LABELS[d.status] || d.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMontant(d.amount)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm" onClick={() => ouvrirFormulaire(d)}>Modifier</button>
                    {peutSupprimer(d) && (
                      <button
                        className="btn btn-sm btn-danger"
                        style={{ marginLeft: 6 }}
                        onClick={() => supprimer(d)}
                      >
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} style={{ fontWeight: 600 }}>Total engagé</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(engage)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProjectBudget;
